import { AxiosProgressEvent } from "axios";
import React from "react";
import { useField, UseFieldConfig, useForm } from "react-final-form";
import { Trans, useTranslation } from "react-i18next";
import AuthService from "src/services/authService";
import ElasticService from "src/services/elasticService";
import { useSelector } from "src/store";
import { selectCurrentWorkspaceId } from "src/store/app";

import { useUploadFileMutation } from "@dashboard/files/api";
import { useUploadContext } from "@dashboard/files/context/UploadContext";
import { FormControl, FormHelperText } from "@mui/material";
import { styled } from "@mui/material/styles";

import { showSuccessToast, showWarningToast } from "../toasts/Toasts";

import { FileDropzone } from "./FileDropzone";

const StyledFormControl = styled(FormControl)(({ theme }) => ({
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.emteria.action,
    },
    "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.emteria.error,
    },
    "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline": {
        backgroundColor: theme.palette.emteria.background.inputDisabled,
    },
}));

const StyledFormHelperText = styled(FormHelperText)(({ theme }) => ({
    color: theme.palette.emteria.action,
    "&.Mui-error": {
        color: theme.palette.emteria.error,
    },
}));

const forbiddenFileTypes = [
    "apk+",
    "apkm",
    "apkx",
    "deb",
    "exe",
    "gif",
    "htm",
    "html",
    "jpeg",
    "jpg",
    "mp3",
    "png",
    "ppt",
    "pptx",
];

interface FormValues {
    files: File[];
    target: number | undefined;
}

type Props = {
    name: string;
    accept?: string;
    className?: string;
    config?: UseFieldConfig;
    disabled?: boolean;
    groupId?: number;
    onGroupSelect?: (files: File[]) => void;
    uploadTargetName?: string;
};

export const UploadFileField = ({
    accept,
    className,
    config,
    disabled,
    groupId,
    name,
    onGroupSelect,
    uploadTargetName,
}: Props) => {
    const { t } = useTranslation(["common"]);
    const inputId = React.useId();

    const form = useForm<FormValues>();

    const { input, meta } = useField<string>(name, { ...config, type: "file" });
    const error = meta.touched && meta.error;
    const user = AuthService.getCurrentUser();

    const { abortController, setUploadStarted, setUploadProgress, setUploadedFileIds, setSelectedFiles } = useUploadContext();
    const [uploadFile] = useUploadFileMutation();
    const workspaceId = useSelector(selectCurrentWorkspaceId);

    const onSubmit = () => {
        const formValues = form.getState().values;
        const files = formValues?.files;

        if (!files?.length || !workspaceId) {
            return;
        }

        setUploadStarted(true);
        let completedFiles = 0;
        const progressPerFile = files.length === 1 ? 0 : 100 / files.length;

        Promise.all(
            files.map((file) => {
                if (abortController.signal.aborted) {
                    throw new Error("Upload canceled");
                }

                return uploadFile({
                    workspaceId: workspaceId,
                    filename: file.name,
                    fileToUpload: file,
                    controller: abortController,
                    groupId: groupId || null,
                    onUploadProgress: (progressEvent: AxiosProgressEvent) => {
                        if (files.length === 1 && progressEvent.total) {
                            const fileProgress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
                            setUploadProgress(fileProgress);
                        } else {
                            const currentProgress = Math.round(completedFiles * progressPerFile);
                            setUploadProgress(currentProgress);
                        }
                    },
                    setUploadedFileIds,
                })
                    .unwrap()
                    .then(() => {
                        completedFiles++;
                        if (files.length > 1) {
                            const newProgress = Math.round(completedFiles * progressPerFile);
                            setUploadProgress(newProgress);
                        }
                        if (!abortController.signal.aborted && user?.email) {
                            showSuccessToast(t("common:section.uploadField.toast.fileUploadedSuccess"));
                            ElasticService.createTransaction("upload", {
                                user_email: user.email,
                                file_name: file.name,
                                file_size: file.size,
                            });
                        }
                    });
            }),
        )
            .catch(() => {
                // Error toast is shown in files API upload mutation (rtkClient).
            })
            .finally(() => {
                setUploadProgress(0);
                setUploadStarted(false);
                setSelectedFiles([]);
                form.reset();
            });
    };

    const handleFileSelection = (files: File[]) => {
        if (onGroupSelect) {
            onGroupSelect(files);
        } else {
            setSelectedFiles(files);
            input.onChange(files);
            onSubmit();
        }
    };

    const handleFileValidation = (files: File[] | null) => {
        if (!files) {
            return false;
        }

        for (const file of files) {
            const fileExtension = file.name.split(".").pop()?.toLowerCase();
            if (forbiddenFileTypes.includes(fileExtension || "")) {
                showWarningToast(t("common:section.uploadField.unsupportedFileType"), { autoClose: 8000 });
                return false;
            }
        }
        return true;
    };

    const onFilesSelected = (files: File[]) => {
        if (!disabled && handleFileValidation(files)) {
            handleFileSelection(files);
        }
    };

    return (
        <StyledFormControl className={className} error={error} style={{ opacity: disabled ? 0.5 : 1 }} fullWidth>
            <FileDropzone
                inputId={inputId}
                label={
                    uploadTargetName ? (
                        <Trans
                            ns="common"
                            i18nKey="section.uploadField.dropzoneWithDestination"
                            values={{ destination: uploadTargetName }}
                            components={{ u: <u /> }}
                        />
                    ) : (
                        <Trans ns="common" i18nKey="section.uploadField.dropzone" components={{ u: <u /> }} />
                    )
                }
                accept={accept}
                caption={t("common:section.uploadField.maxSize")}
                error={error}
                multiple
                onFilesSelected={onFilesSelected}
                disabled={disabled}
            />
            <StyledFormHelperText>{error}</StyledFormHelperText>
        </StyledFormControl>
    );
};
